import { useState } from "react";
import { trpc } from "../../_core/trpc";
import { Panel } from "./Admin";
import { useAuth } from "../../_core/useAuth";
import { publicUrl } from "../../_core/public-url";
function AdminSettings() {
  const settings = trpc.admin.getSettings.useQuery();
  const save = trpc.admin.saveSettings.useMutation();
  const overview = trpc.admin.overview.useQuery();
  const publish = trpc.admin.publish.useMutation();
  const utils = trpc.useUtils();
  const { user, logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [copied, setCopied] = useState(false);
  const s = settings.data;
  const patch = async (input, label) => {
    setStatus(null);
    try {
      await save.mutateAsync(input);
      await utils.admin.getSettings.invalidate();
      await utils.admin.overview.invalidate();
      await utils.public.state.invalidate();
      setStatus({ kind: "ok", msg: `${label} saved.` });
    } catch (e) {
      setStatus({ kind: "err", msg: e instanceof Error ? e.message : "Save failed." });
    }
  };
  const site = publicUrl("/");
  if (!s) {
    return <Panel eyebrow="Settings" title="Loading…">
        <div />
      </Panel>;
  }
  return <Panel
    eyebrow="Settings"
    title="The site itself"
    description="Title, animation feel, gallery transitions, and the account that owns this site."
  >
      {status && <div
    role="status"
    className={`mb-5 rounded-xl border px-4 py-3 text-sm ${status.kind === "ok" ? "border-[rgba(126,217,154,.35)] bg-[rgba(38,120,66,.14)] text-[#a8e6bd]" : "border-[rgba(244,63,94,.4)] bg-[rgba(244,63,94,.14)] text-[#ffc9cd]"}`}
  >
          {status.msg}
        </div>}

      <div className="grid gap-5 lg:grid-cols-2">
        {
    /* ── title ── */
  }
        <div className="glass rounded-xl p-5">
          <p className="eyebrow mb-3">Site title</p>
          <label className="block">
            <input data-testid="site-title"
    className="field-lux"
    defaultValue={s.siteTitle}
    aria-label="Site title"
    onBlur={(e) => {
      const v = e.target.value.trim();
      if (v && v !== s.siteTitle) void patch({ siteTitle: v }, "Title");
    }}
  />
            <span className="mt-1.5 block text-[0.68rem] text-[color:var(--muted-foreground)]">
              Used in the browser tab and when the link is shared.
            </span>
          </label>
        </div>

        {
    /* ── animation intensity ── */
  }
        <div className="glass rounded-xl p-5">
          <p className="eyebrow mb-1">Animation intensity</p>
          <p className="mb-3 text-[0.72rem] leading-relaxed text-[color:var(--muted-foreground)]">
            Controls the 3D camera drift, glow and petal density. Lower is calmer and kinder to older
            phones.
          </p>
          <div className="flex gap-2">
            {[1, 2, 3].map((n) => {
    const active = s.animationIntensity === n;
    return <button
      key={n}
      type="button"
      onClick={() => void patch({ animationIntensity: n }, "Animation")}
      className={`flex-1 rounded-lg border px-2 py-2.5 text-[0.66rem] uppercase tracking-[0.12em] transition ${active ? "border-[rgba(233,168,242,.55)] bg-[rgba(233,168,242,.14)] text-[#e9a8f2]" : "border-[rgba(233,168,242,.16)] text-[color:var(--muted-foreground)] hover:border-[rgba(233,168,242,.32)]"}`}
      aria-pressed={active}
    >
                  {["Gentle", "Balanced", "Cinematic"][n - 1]}
                </button>;
  })}
          </div>
        </div>

        {
    /* ── transitions ── */
  }
        <div className="glass rounded-xl p-5">
          <p className="eyebrow mb-3">Gallery transitions</p>
          <div className="flex flex-wrap gap-2">
            {[
    ["cinematic", "Cinematic"],
    ["fade", "Fade"],
    ["slide", "Slide"]
  ].map(([value, label]) => <button
    key={value}
    type="button"
    onClick={() => void patch({ transitionStyle: value }, "Transition")}
    className={`rounded-full px-3.5 py-2 text-[0.66rem] uppercase tracking-[0.12em] transition ${s.transitionStyle === value ? "bg-[rgba(233,168,242,.18)] text-[#e9a8f2] ring-1 ring-[rgba(233,168,242,.45)]" : "border border-[rgba(233,168,242,.18)] text-[color:var(--muted-foreground)] hover:text-[#dcdff0]"}`}
    aria-pressed={s.transitionStyle === value}
  >
                {label}
              </button>)}
          </div>

          <div className="gold-rule my-4" />

          <button
    type="button"
    onClick={() => void patch({ zoomEffect: !s.zoomEffect }, "Zoom effect")}
    className="flex w-full items-center justify-between gap-3 text-left"
    aria-pressed={s.zoomEffect}
  >
            <span>
              <span className="block text-sm text-[#f3f4f9]">Zoom on hover</span>
              <span className="mt-0.5 block text-[0.68rem] text-[color:var(--muted-foreground)]">
                Photos gently scale up under the pointer. No effect on touch.
              </span>
            </span>
            <span
    className={`relative h-6 w-11 shrink-0 rounded-full transition ${s.zoomEffect ? "bg-[#e9a8f2]" : "bg-white/15"}`}
  >
              <span
    className={`absolute top-0.5 h-5 w-5 rounded-full bg-[#1a0b24] transition-all ${s.zoomEffect ? "left-[22px]" : "left-0.5"}`}
  />
            </span>
          </button>
        </div>

        {
    /* ── account ── */
  }
        <div className="glass rounded-xl p-5">
          <p className="eyebrow mb-3">Owner account</p>
          <p className="truncate text-sm text-[#f3f4f9]">{user?.email}</p>
          <p className="mt-1 text-[0.7rem] leading-relaxed text-[color:var(--muted-foreground)]">
            This is the only account with admin access. There is no password reset by email — if you
            lose the password, the site cannot be recovered from the browser.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" onClick={() => void logout()} className="btn-ghost-lux !min-h-[38px] !px-4 !text-[0.62rem]">
              Sign out
            </button>
          </div>
        </div>
      </div>

      <PasswordPanel />

      {
    /* ── publish ── */
  }
      <div className="glass gold-frame mt-5 rounded-xl p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="eyebrow">Share her link</p>
            <p className="mt-2 max-w-xl text-[0.78rem] leading-relaxed text-[color:var(--muted-foreground)]">
              Everything you save is already live for visitors — the link below always shows the
              current state of the site. Publishing just records the moment you declared it ready.
            </p>
          </div>
          <button
    type="button"
    onClick={async () => {
      setStatus(null);
      try {
        const res = await publish.mutateAsync({});
        await utils.admin.overview.invalidate();
        setStatus({
          kind: "ok",
          msg: `Published at ${new Date(res.publishedAt).toLocaleString()}.`
        });
      } catch (e) {
        setStatus({ kind: "err", msg: e instanceof Error ? e.message : "Publish failed." });
      }
    }}
    className="btn-gold !min-h-[40px] !text-[0.64rem]"
    disabled={publish.isPending}
  >
            {publish.isPending ? "Publishing\u2026" : "Publish"}
          </button>
        </div>

        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <input
    readOnly
    value={site}
    onFocus={(e) => e.currentTarget.select()}
    aria-label="Public link"
    className="field-lux !min-h-[40px] flex-1 truncate !text-[0.78rem]"
  />
          <button
    type="button"
    onClick={async () => {
      try {
        await navigator.clipboard.writeText(site);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2e3);
      } catch {
        setCopied(false);
      }
    }}
    className="btn-ghost-lux !min-h-[40px] !px-4 !text-[0.62rem]"
  >
            {copied ? "Copied \u2713" : "Copy"}
          </button>
          <a href={site} target="_blank" rel="noreferrer" className="btn-ghost-lux !min-h-[40px] !px-4 !text-[0.62rem]">
            Open
          </a>
        </div>

        {overview.data?.publishedAt && <p className="mt-3 text-[0.68rem] text-[color:var(--muted-foreground)]">
            Last published {new Date(overview.data.publishedAt).toLocaleString()}
            {overview.data.publishedBy ? ` by ${overview.data.publishedBy}` : ""}
          </p>}
      </div>

      {
    /* ── honest limits ── */
  }
      <div className="glass mt-5 rounded-xl p-5">
        <p className="eyebrow mb-2">What this site does not do</p>
        <ul className="space-y-1.5 text-[0.75rem] leading-relaxed text-[color:var(--muted-foreground)]">
          <li>• It cannot compress video on the server — clips over 100 MB are rejected with a message.</li>
          <li>• It does not send email, so there is no password-reset link and no reminders. If you
            forget the password, the only way back in is a direct database update.</li>
          <li>• It has no analytics, so it never tracks her or anyone else.</li>
          <li>• It does not connect to streaming services; the song is the file you upload.</li>
        </ul>
      </div>
    </Panel>;
}
function PasswordPanel() {
  const change = trpc.admin.changePassword.useMutation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [msg, setMsg] = useState(null);
  const tooShort = next.length > 0 && next.length < 8;
  const mismatch = confirm.length > 0 && next !== confirm;
  const canSubmit = current.length > 0 && next.length >= 8 && next === confirm && !change.isPending;
  const submit = async () => {
    setMsg(null);
    if (!canSubmit) return;
    try {
      await change.mutateAsync({ currentPassword: current, newPassword: next });
      setCurrent("");
      setNext("");
      setConfirm("");
      setMsg({
        kind: "ok",
        text: "Password changed. You stay signed in here \u2014 use the new password next time you sign in."
      });
    } catch (e) {
      setMsg({
        kind: "err",
        text: e instanceof Error ? e.message : "Could not change the password."
      });
    }
  };
  return <div className="glass mt-5 rounded-xl p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="eyebrow">Change password</p>
          <p className="mt-1.5 max-w-lg text-[0.72rem] leading-relaxed text-[color:var(--muted-foreground)]">
            Rotate it here — no database access needed. Your current password is required, and the
            new one is hashed before it is stored.
          </p>
        </div>
        <button
    type="button"
    onClick={() => setShow((v) => !v)}
    className="btn-ghost-lux !min-h-[36px] !px-4 !text-[0.6rem]"
    aria-expanded={show}
    aria-controls="password-form"
  >
          {show ? "Hide" : "Change"}
        </button>
      </div>

      {show && <form
    id="password-form"
    className="mt-4 grid max-w-lg gap-3"
    onSubmit={(e) => {
      e.preventDefault();
      void submit();
    }}
  >
          <label className="block">
            <span className="mb-1.5 block text-[0.7rem] uppercase tracking-[0.16em] text-[#c9cce0]">
              Current password
            </span>
            <input
    type="password"
    className="field-lux"
    value={current}
    onChange={(e) => setCurrent(e.target.value)}
    autoComplete="current-password"
    required
  />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[0.7rem] uppercase tracking-[0.16em] text-[#c9cce0]">
              New password
            </span>
            <input
    type="password"
    className="field-lux"
    value={next}
    onChange={(e) => setNext(e.target.value)}
    autoComplete="new-password"
    minLength={8}
    aria-invalid={tooShort || void 0}
    aria-describedby="pw-hint"
    required
  />
            <span
    id="pw-hint"
    className={`mt-1.5 block text-[0.68rem] ${tooShort ? "text-[#ffc9cd]" : "text-[color:var(--muted-foreground)]"}`}
  >
              {tooShort ? `${8 - next.length} more character(s) needed.` : "At least 8 characters."}
            </span>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[0.7rem] uppercase tracking-[0.16em] text-[#c9cce0]">
              Confirm new password
            </span>
            <input
    type="password"
    className="field-lux"
    value={confirm}
    onChange={(e) => setConfirm(e.target.value)}
    autoComplete="new-password"
    aria-invalid={mismatch || void 0}
    aria-describedby="pw-confirm-hint"
    required
  />
            {mismatch && <span id="pw-confirm-hint" className="mt-1.5 block text-[0.68rem] text-[#ffc9cd]">
                The two passwords do not match yet.
              </span>}
          </label>

          {msg && <p
    role="status"
    className={`rounded-lg border px-3 py-2 text-[0.78rem] leading-relaxed ${msg.kind === "ok" ? "border-[rgba(126,217,154,.4)] bg-[rgba(38,120,66,.14)] text-[#a8e6bd]" : "border-[rgba(244,63,94,.4)] bg-[rgba(244,63,94,.14)] text-[#ffc9cd]"}`}
  >
              {msg.text}
            </p>}

          <div className="flex items-center gap-3">
            <button type="submit" className="btn-gold !min-h-[42px] !text-[0.64rem]" disabled={!canSubmit}>
              {change.isPending ? "Changing\u2026" : "Change password"}
            </button>
            <span className="text-[0.66rem] text-[color:var(--muted-foreground)]">
              There is no email reset — keep the new one safe.
            </span>
          </div>
        </form>}
    </div>;
}
export {
  AdminSettings as default
};
